// services/audioRecordingService.js

/**
 * Initialize and start audio recording with AudioWorklet
 * @param {Object} socket - The socket.io instance
 * @param {string} konsultasjonId - The ID of the current consultation
 * @returns {Promise<Object>} - An object containing the audio context, stream, and worklet node
 */
export const startRecording = async (socket, konsultasjonId) => {
  try {
    console.log("Starting recording with AudioWorklet...");
    
    // Get audio stream from the user's microphone
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    
    // Create a new AudioContext with a 16kHz sample rate for speech recognition
    const context = new AudioContext({ sampleRate: 16000 });
    await context.resume();
    console.log("AudioContext sampleRate:", context.sampleRate);
    
    // Load the PCM processor audio worklet
    await context.audioWorklet.addModule('/pcm-processor.js');
    
    // Create a media stream source from the microphone input
    const source = context.createMediaStreamSource(stream);
    
    // Create a new AudioWorkletNode using the PCM processor
    const workletNode = new AudioWorkletNode(context, 'pcm-processor');
    
    // Set up the message handler for PCM data from the processor
    workletNode.port.onmessage = (event) => {
      const pcmBuffer = event.data;
      if (socket) {
        socket.emit('audio_data', {
          konsultasjon_id: konsultasjonId,
          audio: pcmBuffer
        });
      }
    };
    
    // Connect the audio source to the worklet node and then to the destination
    source.connect(workletNode);
    workletNode.connect(context.destination);
    
    // Notify the server that recording has started
    if (socket) {
      socket.emit('start_stream', { konsultasjon_id: konsultasjonId });
    }
    
    console.log("Recording started with AudioWorklet.");
    
    return {
      audioContext: context,
      audioStream: stream,
      workletNode: workletNode
    };
  } catch (error) {
    console.error("Error starting recording with AudioWorklet:", error);
    throw error;
  }
};

/**
 * Stop audio recording and clean up resources
 * @param {AudioContext} audioContext - The audio context to close
 * @param {MediaStream} audioStream - The audio stream to stop
 * @param {Object} socket - The socket.io instance
 * @param {string} konsultasjonId - The ID of the current consultation
 */
export const stopRecording = async (audioContext, audioStream, socket, konsultasjonId) => {
  console.log("Stopping recording...");
  
  // Close the audio context if it exists
  if (audioContext) {
    await audioContext.close();
  }
  
  // Stop all tracks in the audio stream if it exists
  if (audioStream) {
    audioStream.getTracks().forEach(track => track.stop());
  }
  
  // Notify the server that recording has stopped
  if (socket) {
    socket.emit('stop_stream', { konsultasjon_id: konsultasjonId });
  }
  
  console.log("Recording stopped.");
};

/**
 * Toggle recording on/off
 * @param {boolean} isRecording - Current recording state
 * @param {Object} socket - The socket.io instance
 * @param {Object} currentKonsultasjon - The current consultation object
 * @param {AudioContext} audioContext - Current audio context if recording
 * @param {MediaStream} audioStream - Current audio stream if recording
 * @param {Function} startNewConsultation - Function to start a new consultation
 * @param {Function} setActiveTab - Function to set the active tab
 * @param {Function} setIsRecording - Function to update recording state
 * @param {Function} setAudioContext - Function to update audio context state
 * @param {Function} setAudioStream - Function to update audio stream state
 */
export const toggleRecording = async (
  isRecording,
  socket,
  currentKonsultasjon,
  audioContext,
  audioStream,
  startNewConsultation,
  setActiveTab,
  setIsRecording,
  setAudioContext,
  setAudioStream
) => {
  if (!currentKonsultasjon) {
    console.log("Ingen konsultasjon valgt. Starter automatisk en ny konsultasjon.");
    await startNewConsultation();
    return;
  }
  
  if (isRecording) {
    await stopRecording(audioContext, audioStream, socket, currentKonsultasjon.id);
    setAudioContext(null);
    setAudioStream(null);
    setIsRecording(false);
  } else {
    try {
      const { audioContext: newContext, audioStream: newStream } = await startRecording(
        socket, 
        currentKonsultasjon.id
      );
      
      setAudioContext(newContext);
      setAudioStream(newStream);
      setIsRecording(true);
      setActiveTab('transkripsjon');
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Kunne ikke starte opptak: " + error.message);
    }
  }
};